import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Row, Col, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from "react-toastify";
import {
    faFileZipper,
    faXmark,
    faTrash
} from "@fortawesome/free-solid-svg-icons";

import useBreakpoint from '../../../hooks/useBreakpoint';
import useUser from '../../../hooks/useUser';
import { Spinner } from 'react-bootstrap';

import GET_OUTGOING_TRAINEESHIP from '../../../graphql/queries/traineeships/getOutgoingTraineeship';
import ACCEPT_TRAINEESHIP from '../../../graphql/mutations/traineeships/acceptTraineeship';
import DECLINE_TRAINEESHIP from '../../../graphql/mutations/traineeships/declineTraineeship';
import DELETE_TRAINEESHIP from '../../../graphql/mutations/traineeships/deleteTraineeship';

import CustomTab from '../../../components/CustomTab';
import CustomButton from '../../../components/CustomButton';
import CustomModal from '../../../components/CustomModal';
import TraineeshipInfoTable from './components/TraineeshipInfoTable';
import StudentInfo from './components/StudentInfo';
import InstitutionInfoTable from './components/InstitutionInfoTable';
import TraineeshipProgrammeTable from './components/TraineeshipProgrammeTable';
import TraineeshipRecognitionTable from './components/TraineeshipRecognitionTable';
import TraineeshipSupportTable from './components/TraineeshipSupportTable';
import TraineeshipChangesTable from './components/TraineeshipChangesTable';
import { StatusActionPair } from '../../../data/statusActionPairs';
import COLORS from '../../../data/colors';
import toastConfiguration from '../../../data/toastConfiguration';
import CertificateTable from './components/CertificateTable';

import "react-toastify/dist/ReactToastify.css";
import './index.scss';

const SingleViewTraineeship = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'learningAgreements' });

    const [size] = useBreakpoint();
    const { traineeshipID } = useParams();
    const { user } = useUser();
    let location = useLocation();
    const navigate = useNavigate();
    const [showModalReject, setShowModalReject] = useState(false);
    const [showModalSign, setShowModalSign] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [rejectionMessage, setRejectionMessage] = useState("");
    const [signed, setSigned] = useState(false);

    const { loading, data, error, refetch } = useQuery(GET_OUTGOING_TRAINEESHIP, {
        variables: { id: traineeshipID }
    });

    const [
        acceptTraineeshipMutation,
        {
            loading: loadingAcceptTraineeship,
            error: errorAcceptTraineeshipMutation,
        },
    ] = useMutation(ACCEPT_TRAINEESHIP);

    const [
        declineTraineeshipMutation,
        {
            loading: loadingDeclineTraineeship,
            error: errorDeclineTraineeshipMutation,
        },
    ] = useMutation(DECLINE_TRAINEESHIP);

    const [
        deleteTraineeshipMutation,
        {
            loading: loadingDeleteTraineeship,
            error: errorDeleteTraineeshipMutation,
        },
    ] = useMutation(DELETE_TRAINEESHIP);

    useEffect(() => {
        if (data) {
            // Always check for sending institution (outgoing)
            if (user?.erasmusCode !== data?.traineeship?.sending_institution?.field_hei_erasmus_code) {
                navigate("/unauthorized");
            }
        }
    }, [data]);

    const position = "Responsible person at the Sending Institution";
    const typeOfStudent = "outgoing"; // Always outgoing for traineeships
    const institution = 1; // Always 1 for outgoing/sending institution

    const shouldDisableSignButtons = (
      signed ||
      StatusActionPair["traineeship-typeOfStudent-1-" + data?.traineeship?.field_tr_status] !== "act" ||
      user?.permissions.find((permission) => permission.value === "managingStudents") === undefined
  );

    const handleCloseModalSign = () => setShowModalSign(false);
    const handleShowModalSign = () => setShowModalSign(true);
    const handleCloseModalReject = () => setShowModalReject(false);
    const handleShowModalReject = () => setShowModalReject(true);
    const handleCloseModalDelete = () => setShowModalDelete(false);
    const handleShowModalDelete = () => setShowModalDelete(true);

    const handleSignButtonClick = async () => {
        const toastId = toast.loading("Signing Traineeship...", toastConfiguration);
        setSigned(true);
        try {
            let result = await acceptTraineeshipMutation({
              "variables": {
                "email": user?.email,
                "institution": institution,
                "name": user?.userName ? user?.userName : user?.email,
                "traineeshipID": data?.traineeship?.traineeshipID,
                "position": position,
                "status": data?.traineeship?.field_tr_status
              }
            });
            handleCloseModalSign();
            if (!result?.errors) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: "Successfully signed OLA Traineeship",
                    type: "success",
                    isLoading: false,
                });
                refetch();
            }
        } catch (error) {
            setSigned(false);
            toast.update(toastId, {
                ...toastConfiguration,
                render: "Error on signing. Please try again.",
                type: "error",
                isLoading: false,
            });
        }
    };

    const handleDeclineButtonClick = async () => {
        const toastId = toast.loading("Declining Traineeship...", toastConfiguration);
        setSigned(true);
        try {
            let result = await declineTraineeshipMutation({
              "variables": {
                "email": user?.email,
                "institution": institution,
                "name": user?.userName ? user?.userName : user?.email,
                "traineeshipID": data?.traineeship?.traineeshipID,
                "reason": rejectionMessage,
                "position": position,
                "status": data?.traineeship?.field_tr_status
              }
            });
            handleCloseModalReject();
            if (!result?.errors) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: "Successfully declined OLA Traineeship",
                    type: "success",
                    isLoading: false,
                });
                refetch();
            }
        } catch (error) {
            setSigned(false);
            toast.update(toastId, {
                ...toastConfiguration,
                render: "Error on declining. Please try again.",
                type: "error",
                isLoading: false,
            });
        }
    };

    const shouldDisableDeleteButton = () => {
        // Check if user has required permissions
        if (user?.permissions.find(permission => permission.value === "managingStudents") === undefined) {
            return true;
        }

        const traineeshipStatus = data?.traineeship?.field_tr_status;
        const preliminaryCommitments = data?.traineeship?.commitment_preliminary || [];

        // Allow deletion if:
        // 1. Status is 'preliminary_signed-by-student' OR 'Signed by student' OR 'signed_by_student' OR
        // 2. Status is 'preliminary_unsigned' OR 'unsigned' OR 'draft' AND no preliminary commitments exist
        const canDelete =
        traineeshipStatus === 'preliminary_signed-by-student' ||
        traineeshipStatus === 'Signed by student' ||
        traineeshipStatus === 'signed by student' ||
        traineeshipStatus === 'signed_by_student' ||
        ((traineeshipStatus === 'preliminary_unsigned' ||
            traineeshipStatus === 'unsigned' ||
            traineeshipStatus === 'draft') &&
            preliminaryCommitments.length === 0);

        return !canDelete;
    };

    const handleDeleteButtonClick = async () => {
        const toastId = toast.loading("Deleting Traineeship...", toastConfiguration);

        try {
            let result = await deleteTraineeshipMutation({
                variables: {
                    traineeshipID: data?.traineeship?.traineeshipID,
                    pathName: "outgoing" // Always outgoing for traineeships
                }
            });

            handleCloseModalDelete();

            if (!result?.errors && result?.data?.deleteTraineeship?.success) {
                toast.update(toastId, {
                    ...toastConfiguration,
                    render: "OLA Traineeship successfully deleted",
                    type: "success",
                    isLoading: false,
                });
                navigate("/traineeships/outgoing");
            }
        } catch (error) {
            toast.update(toastId, {
                ...toastConfiguration,
                render: "Failed to delete OLA Traineeship. Please try again.",
                type: "error",
                isLoading: false,
            });
        }
    };

    const hasCertificateData = () => {
        const tableD = data?.traineeship?.table_d;
        if (!tableD) return false;
        
        // Check if table_d exists and has at least one non-empty field
        const hasData = Object.values(tableD).some(value => {
            if (value === null || value === undefined) return false;
            if (typeof value === 'string') return value.trim() !== '';
            return true;
        });
        
        return hasData;
    };

    if (error)
        return (
            <div className="text-center text-danger mx-auto">{t('errorMessage')}</div>
        );

    if (loading) {
        return (
            <div
                style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)"
                }}
            >
                <Spinner
                    as="div"
                    animation="grow"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="align-middle my-auto"
                />
                <span>{t('loadingMessage')}</span>
            </div>
        )
    }

    return (
        <>
            <Row style={size !== "md" && size !== "lg" && size !== "xl"? { marginLeft: 35, marginRight: 35 } : { marginLeft: 0, marginRight: 0 }}>
                <Col style={{ marginTop: 75 }}>
                    <Tabs id="application-info">
                        <CustomTab eventKey="home" title={"TRAINEESHIP INFO"} style={{ background: "#ffff" }}>
                            <TraineeshipInfoTable data={data} size={size} typeOfStudent="outgoing" />
                        </CustomTab>
                    </Tabs>
                </Col>
            </Row>
            
            <Row style={size !== "md" && size !== "lg" && size !== "xl" ? { marginLeft: 35, marginRight: 35 } : { marginLeft: 0, marginRight: 0 }}>
                <Col style={{ marginTop: 36 }}>
                    <Tabs id="second-tab">
                        {/* General Info Tab */}
                        <CustomTab eventKey="general-info" id="general-info" title={t('generalInfo.title')} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 10 }}>
                                    <Tabs defaultActiveKey="home" id="student-info">
                                        <CustomTab eventKey="home" title={t('generalInfo.studentPersonalData.title')} style={{ background: "#ffff" }}>
                                            <StudentInfo data={data} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col md={6} style={{ marginTop: 33, marginBottom: 10 }}>
                                    <Tabs defaultActiveKey="home" id="sending-hei">
                                        <CustomTab eventKey="home" title={"Sending Institution"} style={{ background: "#ffff" }}>
                                            <InstitutionInfoTable
                                                type={"sending"}
                                                size={size}
                                                data={data?.traineeship?.sending_institution}
                                                contact_person={data?.traineeship?.sending_contact_person}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                                <Col md={6} style={{ marginTop: 33, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="receiving-org">
                                        <CustomTab eventKey="home" title={"Receiving Organisation"} style={{ background: "#ffff" }}>
                                            <InstitutionInfoTable
                                                type={"receiving"}
                                                size={size}
                                                data={data?.traineeship?.receiving_organization}
                                                contact_person={data?.traineeship?.receiving_contact_person}
                                                receiving_mentor={data?.traineeship?.receiving_mentor}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Traineeship Programme Tab (Table A) */}
                        <CustomTab eventKey="programme" id="programme" title={"Traineeship Programme"} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="programme-details">
                                        <CustomTab eventKey="home" title={"Programme Details (Table A)"} style={{ background: "#ffff" }}>
                                            <TraineeshipProgrammeTable data={data?.traineeship?.table_a} size={size} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Changes to Traineeship Programme Tab (Table A2) */}
                        <CustomTab eventKey="changes" id="changes" title={"Changes Traineeship Programme"} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="changes-details">
                                        <CustomTab eventKey="home" title={"Changes to Table A"} style={{ background: "#ffff" }}>
                                            <TraineeshipChangesTable data={data?.traineeship?.final_table_a2} size={size} />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Recognition & Support Tab (Tables B & C) */}
                        <CustomTab eventKey="recognition" id="recognition" title={"Recognition & Support"} style={{ background: "#EAECF3" }}>
                            <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                <Col md={6} style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="recognition-details">
                                        <CustomTab eventKey="home" title={"Sending Institution (Table B)"} style={{ background: "#ffff" }}>
                                            <TraineeshipRecognitionTable
                                                data={data?.traineeship?.table_b}
                                                type={data?.traineeship?.table_b_type?.name}
                                                accidentInsurance={data?.traineeship?.table_b_accident_insurance}
                                                size={size}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                                <Col md={6} style={{ marginTop: 36, marginBottom: 36 }}>
                                    <Tabs defaultActiveKey="home" id="support-details">
                                        <CustomTab eventKey="home" title={"Receiving Organisation (Table C)"} style={{ background: "#ffff" }}>
                                            <TraineeshipSupportTable
                                                data={data?.traineeship?.table_c}
                                                accidentInsurance={data?.traineeship?.table_c_accident_insurance}
                                                size={size}
                                            />
                                        </CustomTab>
                                    </Tabs>
                                </Col>
                            </Row>
                        </CustomTab>

                        {/* Certificate Tab - Only show if certificate data exists */}
                        {hasCertificateData() && (
                            <CustomTab eventKey="certificate" id="certificate" title={"Traineeship Certificate"} style={{ background: "#EAECF3" }}>
                                <Row style={{ marginLeft: 5, marginRight: 5 }}>
                                    <Col style={{ marginTop: 36, marginBottom: 36 }}>
                                        <Tabs defaultActiveKey="home" id="certificate-details">
                                            <CustomTab eventKey="home" title={"Certificate (Table D)"} style={{ background: "#ffff" }}>
                                                <CertificateTable data={data?.traineeship?.table_d} size={size} />
                                            </CustomTab>
                                        </Tabs>
                                    </Col>
                                </Row>
                            </CustomTab>
                        )}
                    </Tabs>
                </Col>
            </Row>

            {!(size !== "md" && size !== "lg" && size !== "xl" && size !== "xxl") ?
                <Row style={{ marginLeft: 35, marginRight: 35 }}>
                    <Col style={{ marginTop: 36, display: "flex", justifyContent: "flex-end" }}>
                        <CustomButton
                            disabled={shouldDisableSignButtons}
                            buttonType={"decline"}
                            text={t('modalReject.declineButton')}
                            icon={faXmark}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            handleClick={handleShowModalReject}
                        />
                        <CustomButton
                            disabled={shouldDisableSignButtons}
                            buttonType={"accept"}
                            text={"Sign Traineeship"}
                            styles={{ marginLeft: 20 }}
                            icon={faFileZipper}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            handleClick={handleShowModalSign}
                        />
                        <CustomButton
                            disabled={shouldDisableDeleteButton()}
                            buttonType={"delete"}
                            text={t('modalDelete.deleteButton')}
                            icon={faTrash}
                            hasIcon={true}
                            iconColor={COLORS.white}
                            handleClick={handleShowModalDelete}
                            styles={{ marginLeft: 20 }}
                        />
                    </Col>
                </Row>
                :
                <Row>
                    <Col style={{ marginTop: 15, paddingLeft: 0, paddingRight: 0 }} xs={4} sm={4}>
                        <div className="col-12">
                            <CustomButton
                                styles={{ width: "100%" }}
                                disabled={shouldDisableSignButtons}
                                buttonType={"decline"}
                                text={"Decline"}
                                icon={faXmark}
                                hasIcon={true}
                                iconColor={COLORS.white}
                                handleClick={handleShowModalReject}
                            />
                        </div>
                    </Col>
                    <Col style={{ marginTop: 15, paddingLeft: 5, paddingRight: 5 }} xs={4} sm={4}>
                        <div className="col-12">
                            <CustomButton
                                buttonType={"accept"}
                                text={"Sign"}
                                icon={faFileZipper}
                                hasIcon={true}
                                iconColor={COLORS.white}
                                handleClick={handleShowModalSign}
                                styles={{ width: "100%" }}
                                disabled={shouldDisableSignButtons}
                            />
                        </div>
                    </Col>
                    <Col style={{ marginTop: 15, paddingLeft: 0, display: "flex", justifyContent: "flex-end", paddingRight: 0 }} xs={4} sm={4}>
                        <div className="col-12">
                            <CustomButton
                                styles={{ width: "100%" }}
                                disabled={shouldDisableDeleteButton()}
                                buttonType={"delete"}
                                text={t('modalDelete.deleteButton')}
                                icon={faTrash}
                                hasIcon={true}
                                iconColor={COLORS.white}
                                handleClick={handleShowModalDelete}
                            />
                        </div>
                    </Col>
                </Row>
            }

          {/* Modals */}
          <CustomModal
              show={showModalReject}
              handleClose={handleCloseModalReject}
              fullscreen={false}
              body={
                  <div>
                      After declining the OLA Traineeship, the student will be able to edit it and introduce your proposed changes. Please fill the comments you want to be emailed to the student in field below:<br />
                      <div style={{ background: "#EAECF3", padding: "5%" }}>
                          <h6 className='h6-style'>{t('modalReject.body.part2')}</h6>
                          <textarea
                              style={{ width: "100%" }}
                              rows={3}
                              value={rejectionMessage}
                              onChange={(e) => setRejectionMessage(e.target.value)}
                          />
                          <h6 style={{ color: "#6C757D" }}>{t('modalReject.body.part3')}</h6>
                      </div>
                  </div>
              }
              button={
                  <CustomButton
                      disabled={!rejectionMessage || loadingDeclineTraineeship}
                      buttonType={"decline"}
                      text={t('modalReject.declineButton')}
                      icon={faXmark}
                      hasIcon={true}
                      iconColor={COLORS.white}
                      handleClick={handleDeclineButtonClick}
                  />
              }
              title={
                  <div>
                      You are about to
                      <span style={{ color: "red" }}> decline </span>
                      a student's OLA Traineeship
                  </div>
              }
          />

          <CustomModal
              show={showModalSign}
              handleClose={handleCloseModalSign}
              fullscreen={false}
              body={<div>By digitally signing this document, the student, the Sending Institution and the Receiving Institution confirm that they approve the Learning Agreement and that they will comply with all the arrangements agreed by all parties. The Sending Institution and the Receiving Organisation/Enterprise undertake to apply all the principles of the Erasmus Charter for Higher Education relating to mobility for traineeships (or the principles agreed in the applicable inter-institutional or cooperation agreement, where relevant). The Beneficiary Institution and the student also commit to what is set out in the Erasmus+ grant agreement. The Receiving Organisation/Enterprise confirms that the traineeship programme, including the planned tasks, learning outcomes, supervision and mentoring arrangements, and assessment, is feasible and will be provided as described. The Sending Institution commits to recognise the traineeship according to the provisions in Table. The student and the Receiving Organisation/Enterprise will communicate to the Sending Institution any problems or changes regarding the traineeship programme, responsible persons and/or traineeship period.</div>}
              button={
                  <CustomButton
                      disabled={loadingAcceptTraineeship}
                      buttonType={"accept"}
                      text={"Sign Traineeship"}
                      hasIcon={true}
                      iconColor={COLORS.white}
                      icon={faFileZipper}
                      handleClick={handleSignButtonClick}
                  />
              }
              title={
                  <div style={{ paddingBottom: "20px" }}>
                      You are about to
                      <span style={{ color: "green" }}> sign </span>
                      a student's OLA Traineeship
                  </div>
              }
          />

          <CustomModal
              show={showModalDelete}
              handleClose={handleCloseModalDelete}
              fullscreen={false}
              body={
                  <div>
                      Are you sure you want to delete this OLA Traineeship? This action cannot be undone.
                  </div>
              }
              button={
                  <CustomButton
                      disabled={loadingDeleteTraineeship}
                      buttonType={"delete"}
                      text={"Delete Traineeship"}
                      icon={faTrash}
                      hasIcon={true}
                      iconColor={COLORS.white}
                      handleClick={handleDeleteButtonClick}
                  />
              }
              title={
                  <div>
                      Are you sure you want to
                      <span style={{ color: "red" }}> delete </span>
                      this OLA Traineeship?
                  </div>
              }
          />

          <ToastContainer
              position="bottom-left"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
          />
      </>
    );

};

export default SingleViewTraineeship;